/**
 * 전년도·기정(확정)·최종 금액 파생
 * - 편성요구/조정 라인에 복사·저장하지 않음 (조회 시 attach / 저장 시 strip)
 * - finalBudgetMode: sumDelta | lastRound
 * - 최종 산정 대상: 본·추경 본편·성립전·간주 (모두 포함)
 * - sumDelta 현액에서만 성립전→추경 흡수 (간주는 구분 유지) — balanceStore
 *
 * SSOT: D:\295\erp\BD\design\08_차수증감_기정최종_전년옵션.md
 */

// 파생금액 계산은 화면 렌더 경로(동기)에서 호출되므로
// API 모드에서는 load* 가 채운 메모리 캐시, 로컬 모드에서는 localStorage 를 동기로 읽는다.
import { getCachedExpenseRequests } from 'budget/budgetExpenseRequestStore';
import { getCachedIncomeRequests } from 'budget/budgetIncomeRequestStore';
import { getCachedExpenseAdjusts } from 'budget/budgetExpenseAdjustStore';
import { getCachedIncomeAdjusts } from 'budget/budgetIncomeAdjustStore';
import {
  BUDGET_CLASS,
  MAIN_KIND_CODE,
  getCachedBudgetRounds,
  normalizeBudgetClass,
  parseSuppKindCode,
  settleParentCode,
  computeFinalAdjNo,
  getBudgetRoundByKindCode,
} from 'budget/budgetRoundStore';
import {
  PRIOR_YEAR_BASIS,
  FINAL_BUDGET_MODE,
  getPriorYearBasis,
  getFinalBudgetMode,
} from 'budget/budgetTenantSettingsStore';

/** 저장 페이로드에서 제거할 파생 필드 */
export const BASELINE_DERIVED_FIELDS = ['prevYearAmt', 'confirmedAmt', 'finalAmt'];

/**
 * 차원 조인 키 (라인 PK 아님)
 * @param {object} row
 * @param {{ fiscalYear?: string|number, acctClass?: string }} [dims]
 */
export function buildBudgetDimKey(row, dims = {}) {
  const acct =
    String(row?.acctClassCode || row?.acctClass || dims.acctClass || '').trim();
  const fund =
    String(row?.fundUid || row?.fundPkgUid || row?.fundCode || '').trim();
  return [
    acct,
    String(row?.deptCode || '').trim(),
    String(row?.projectCode || '').trim(),
    String(row?.subjectCode || row?.subjectUid || '').trim(),
    fund,
    String(row?.budgetType || dims.budgetType || '').trim(),
  ].join('|');
}

/** 성립전예산 — 추경 + preSeq≥1 (211…) */
export function isSeonglipjeonRound(round) {
  if (!round || round.mod === 'D') return false;
  if (normalizeBudgetClass(round.budgetClass) !== BUDGET_CLASS.SUPP) return false;
  const pre = Number(round.preSeq);
  if (Number.isFinite(pre) && pre > 0) return true;
  const parsed = parseSuppKindCode(round.budgetKindCode);
  return !!(parsed && parsed.preSeq > 0);
}

/** @deprecated */
export function isSpare1Round(round) {
  return isSeonglipjeonRound(round);
}

/** 간주예산 — class `d` (301…) */
export function isGanjuRound(round) {
  if (!round || round.mod === 'D') return false;
  return normalizeBudgetClass(round.budgetClass) === BUDGET_CLASS.DEEMED;
}

/** @deprecated */
export function isSpare2Round(round) {
  return isGanjuRound(round);
}

/**
 * 본예산 · 추경 본편만 (성립전 계상 부모·현액 흡수 대상)
 */
export function isMainFinalRound(round) {
  if (!round || round.mod === 'D') return false;
  if (isSeonglipjeonRound(round) || isGanjuRound(round)) return false;
  const cls = normalizeBudgetClass(round.budgetClass);
  if (cls === BUDGET_CLASS.MAIN) return true;
  if (cls === BUDGET_CLASS.SUPP) {
    const pre = Number(round.preSeq);
    if (Number.isFinite(pre)) return pre === 0;
    const parsed = parseSuppKindCode(round.budgetKindCode);
    return parsed ? parsed.preSeq === 0 : false;
  }
  return false;
}

/**
 * 예산 최종·기정 산정 대상 (본·추경본편·성립전·간주)
 */
export function isFinalBudgetRound(round) {
  if (!round || round.mod === 'D' || !round.budgetKindCode) return false;
  return (
    isMainFinalRound(round) || isSeonglipjeonRound(round) || isGanjuRound(round)
  );
}

/** @deprecated → isFinalBudgetRound */
export function isBaselineEligibleRound(round) {
  return isFinalBudgetRound(round);
}

/** 성립전 → 계상 대상 추경 본편 결합코드 (221 → 220) */
export function getSettleParentKindCode(roundOrKind) {
  const kind =
    typeof roundOrKind === 'string'
      ? roundOrKind
      : String(roundOrKind?.budgetKindCode || '');
  const round =
    typeof roundOrKind === 'object' && roundOrKind ? roundOrKind : null;
  if (round && !isSeonglipjeonRound(round)) return '';
  const parsed = parseSuppKindCode(kind);
  if (!parsed || parsed.preSeq <= 0) return '';
  return settleParentCode(parsed.classSeq) || '';
}

/**
 * 현재 차수 미만 · 예산확정 · 최종 산정 대상 전체
 */
export function listConfirmedPriorFinalRounds(tenantCode, fiscalYear, currentKindCode) {
  const cur = String(currentKindCode || '').trim();
  if (!cur) return [];
  return getCachedBudgetRounds(tenantCode, fiscalYear)
    .filter((r) => isFinalBudgetRound(r))
    .filter((r) => String(r.budgetConfirmYn || '') === 'Y')
    .filter((r) => String(r.budgetKindCode).localeCompare(cur) < 0)
    .slice()
    .sort((a, b) => String(a.budgetKindCode).localeCompare(String(b.budgetKindCode)));
}

/** @deprecated 이름 유지 — 내부는 최종 대상 전체 */
export function listConfirmedPriorMainRounds(tenantCode, fiscalYear, currentKindCode) {
  return listConfirmedPriorFinalRounds(tenantCode, fiscalYear, currentKindCode);
}

/** @deprecated */
export function listConfirmedPriorRounds(tenantCode, fiscalYear, currentKindCode) {
  return listConfirmedPriorFinalRounds(tenantCode, fiscalYear, currentKindCode);
}

/**
 * 연도 내 확정 · 최종 산정 대상
 * @param {string} [upToKindCode] 지정 시 해당 코드 이하(포함)
 */
export function listConfirmedFinalRounds(tenantCode, fiscalYear, upToKindCode = '') {
  const upTo = String(upToKindCode || '').trim();
  return getCachedBudgetRounds(tenantCode, fiscalYear)
    .filter((r) => isFinalBudgetRound(r))
    .filter((r) => String(r.budgetConfirmYn || '') === 'Y')
    .filter((r) => !upTo || String(r.budgetKindCode).localeCompare(upTo) <= 0)
    .slice()
    .sort((a, b) => String(a.budgetKindCode).localeCompare(String(b.budgetKindCode)));
}

/** 본·추경 본편만 (현액 흡수 부모 판별 등) */
export function listConfirmedMainRounds(tenantCode, fiscalYear, upToKindCode = '') {
  const upTo = String(upToKindCode || '').trim();
  return getCachedBudgetRounds(tenantCode, fiscalYear)
    .filter((r) => isMainFinalRound(r))
    .filter((r) => String(r.budgetConfirmYn || '') === 'Y')
    .filter((r) => !upTo || String(r.budgetKindCode).localeCompare(upTo) <= 0)
    .slice()
    .sort((a, b) => String(a.budgetKindCode).localeCompare(String(b.budgetKindCode)));
}

/** @deprecated */
export function listConfirmedYearRounds(tenantCode, fiscalYear) {
  return listConfirmedFinalRounds(tenantCode, fiscalYear);
}

/** 확정된 성립전 중 특정 추경 본편에 계상되는 목록 */
export function listConfirmedSeonglipjeonForParent(tenantCode, fiscalYear, parentKindCode) {
  const parent = String(parentKindCode || '').trim();
  if (!parent) return [];
  return listConfirmedFinalRounds(tenantCode, fiscalYear).filter(
    (r) => isSeonglipjeonRound(r) && getSettleParentKindCode(r) === parent
  );
}

/**
 * 차수 편성액 로드 — 조정확정 최종차수 우선, 없으면 요구
 */
export function loadRoundFormationRows(tenantCode, fiscalYear, budgetKindCode, inoutDiv = '지출') {
  const kind = String(budgetKindCode || '').trim();
  if (!kind) return [];
  const round = getBudgetRoundByKindCode(tenantCode, fiscalYear, kind);
  const adjNo =
    round?.finalAdjNo != null && round.finalAdjNo !== ''
      ? Number(round.finalAdjNo)
      : computeFinalAdjNo(round?.adjStages || []);
  const base = {
    tenantCode,
    fiscalYear,
    budgetKindCode: kind,
    acctClass: '',
  };
  const isIncome = String(inoutDiv || '').trim() === '수입';
  if (adjNo != null && Number.isFinite(adjNo)) {
    const adjDims = { ...base, adjNo };
    const adjRows = isIncome ? getCachedIncomeAdjusts(adjDims) : getCachedExpenseAdjusts(adjDims);
    if (adjRows.length) return adjRows.filter((r) => r && r.mod !== 'D');
  }
  const reqRows = isIncome ? getCachedIncomeRequests(base) : getCachedExpenseRequests(base);
  return (reqRows || []).filter((r) => r && r.mod !== 'D');
}

/**
 * 여러 차수 편성금액을 차원키 → 합계 Map
 */
export function sumFormationAmtsByDim(tenantCode, fiscalYear, kindCodes, filterDims = {}) {
  const map = new Map();
  const acctFilter = String(filterDims.acctClass || '').trim();
  const inoutDiv = filterDims.inoutDiv || '지출';
  const preferAdjust = filterDims.preferAdjust !== false;
  (kindCodes || []).forEach((kind) => {
    const code = String(kind || '').trim();
    if (!code) return;
    const rows = preferAdjust
      ? loadRoundFormationRows(tenantCode, fiscalYear, code, inoutDiv)
      : (inoutDiv === '수입' ? getCachedIncomeRequests : getCachedExpenseRequests)({
          tenantCode,
          fiscalYear,
          budgetKindCode: code,
          acctClass: '',
        });
    (rows || []).forEach((r) => {
      if (r.mod === 'D') return;
      if (acctFilter) {
        const rowAcct = String(r.acctClassCode || r.acctClass || '').trim();
        if (rowAcct && rowAcct !== acctFilter) return;
      }
      const key = buildBudgetDimKey(r, filterDims);
      const amt = Number(r.budgetAmt) || 0;
      map.set(key, (map.get(key) || 0) + amt);
    });
  });
  return map;
}

/** @deprecated */
export function sumExpenseAmtsByDim(tenantCode, fiscalYear, kindCodes, filterDims = {}) {
  return sumFormationAmtsByDim(tenantCode, fiscalYear, kindCodes, {
    ...filterDims,
    preferAdjust: false,
  });
}

export function mergeDimMapsByFinalMode(tenantCode, mapsInOrder, mode) {
  const m = mode || getFinalBudgetMode(tenantCode);
  if (!mapsInOrder?.length) return new Map();
  if (m === FINAL_BUDGET_MODE.LAST_ROUND) {
    return pickLastDimMap(mapsInOrder);
  }
  return mergeDimMapsSum(mapsInOrder);
}

function mergeDimMapsSum(maps) {
  const out = new Map();
  (maps || []).forEach((map) => {
    if (!map) return;
    map.forEach((amt, key) => {
      out.set(key, (out.get(key) || 0) + (Number(amt) || 0));
    });
  });
  return out;
}

function pickLastDimMap(maps) {
  if (!maps?.length) return new Map();
  return new Map(maps[maps.length - 1] || []);
}

/**
 * 확정 차수 목록 → 모드별 금액 Map (예산 최종)
 */
export function buildFinalAmtMapFromRounds(
  tenantCode,
  fiscalYear,
  rounds,
  filterDims = {},
  mode
) {
  const m = mode || getFinalBudgetMode(tenantCode);
  const kinds = (rounds || []).map((r) => String(r.budgetKindCode));
  if (!kinds.length) return new Map();
  if (m === FINAL_BUDGET_MODE.LAST_ROUND) {
    const last = kinds[kinds.length - 1];
    return sumFormationAmtsByDim(tenantCode, fiscalYear, [last], filterDims);
  }
  return sumFormationAmtsByDim(tenantCode, fiscalYear, kinds, filterDims);
}

/** 시점 T까지의 예산 최종 Map */
export function buildYearFinalAmtMap(tenantCode, fiscalYear, upToKindCode, filterDims = {}) {
  const rounds = listConfirmedFinalRounds(tenantCode, fiscalYear, upToKindCode);
  return buildFinalAmtMapFromRounds(
    tenantCode,
    fiscalYear,
    rounds,
    filterDims,
    getFinalBudgetMode(tenantCode)
  );
}

/** 전년도 예산액 Map */
export function buildPrevYearAmtMap(tenantCode, fiscalYear, filterDims = {}) {
  const y = Number(fiscalYear);
  if (!Number.isFinite(y)) return new Map();
  const prevY = String(y - 1);
  const basis = getPriorYearBasis(tenantCode);
  if (basis === PRIOR_YEAR_BASIS.YEAR_FINAL) {
    const rounds = listConfirmedFinalRounds(tenantCode, prevY);
    if (rounds.length) {
      return buildFinalAmtMapFromRounds(
        tenantCode,
        prevY,
        rounds,
        filterDims,
        getFinalBudgetMode(tenantCode)
      );
    }
    return sumFormationAmtsByDim(tenantCode, prevY, [MAIN_KIND_CODE], {
      ...filterDims,
      preferAdjust: false,
    });
  }
  return sumFormationAmtsByDim(tenantCode, prevY, [MAIN_KIND_CODE], {
    ...filterDims,
    preferAdjust: false,
  });
}

/**
 * 당해 기정 Map — 현재 차수 이전 확정(본·추·성립전·간주)
 * - sumDelta: 이전 확정 합
 * - lastRound: 직전 확정 1개 차수 절대액
 */
export function buildConfirmedAmtMap(tenantCode, fiscalYear, currentKindCode, filterDims = {}) {
  const priors = listConfirmedPriorFinalRounds(tenantCode, fiscalYear, currentKindCode);
  if (!priors.length) return new Map();
  const mode = getFinalBudgetMode(tenantCode);
  if (mode === FINAL_BUDGET_MODE.LAST_ROUND) {
    const last = priors[priors.length - 1];
    return sumFormationAmtsByDim(
      tenantCode,
      fiscalYear,
      [String(last.budgetKindCode)],
      filterDims
    );
  }
  return sumFormationAmtsByDim(
    tenantCode,
    fiscalYear,
    priors.map((r) => String(r.budgetKindCode)),
    filterDims
  );
}

/**
 * 최종액 파생
 * - sumDelta: 기정 + 금회(증감)
 * - lastRound: 금회(절대액)
 */
export function deriveFinalAmt(confirmedAmt, budgetAmt, { tenantCode } = {}) {
  const budget = Number(budgetAmt) || 0;
  const confirmed = Number(confirmedAmt) || 0;
  if (getFinalBudgetMode(tenantCode) === FINAL_BUDGET_MODE.LAST_ROUND) {
    return budget;
  }
  return confirmed + budget;
}

/** 직전 확정 차수 (lastRound 불러오기용) */
export function getPriorConfirmedRound(tenantCode, fiscalYear, currentKindCode) {
  const priors = listConfirmedPriorFinalRounds(tenantCode, fiscalYear, currentKindCode);
  return priors.length ? priors[priors.length - 1] : null;
}

/**
 * lastRound: 직전 차수 편성 행을 금회 절대액 초안용으로 반환
 */
export function loadPriorRoundRowsForLastRound(
  tenantCode,
  fiscalYear,
  currentKindCode,
  inoutDiv = '지출'
) {
  const prior = getPriorConfirmedRound(tenantCode, fiscalYear, currentKindCode);
  if (!prior) return { prior: null, rows: [] };
  const rows = loadRoundFormationRows(
    tenantCode,
    fiscalYear,
    prior.budgetKindCode,
    inoutDiv
  );
  return { prior, rows };
}

/**
 * 라인에 파생 금액 부착
 */
export function attachBaselineAmounts(lines, dims) {
  const tenant = dims?.tenantCode;
  const year = dims?.fiscalYear;
  const kind = String(dims?.budgetKindCode || '').trim();
  const filterDims = {
    acctClass: dims?.acctClass || '',
    budgetType: dims?.budgetType,
    inoutDiv: dims?.inoutDiv || '지출',
  };
  const prevMap = buildPrevYearAmtMap(tenant, year, filterDims);
  const confMap = kind ? buildConfirmedAmtMap(tenant, year, kind, filterDims) : new Map();

  return (lines || []).map((row) => {
    const key = buildBudgetDimKey(row, filterDims);
    const prevYearAmt = Number(prevMap.get(key)) || 0;
    const confirmedAmt = Number(confMap.get(key)) || 0;
    const budgetAmt = Number(row.budgetAmt) || 0;
    return {
      ...row,
      prevYearAmt,
      confirmedAmt,
      finalAmt: deriveFinalAmt(confirmedAmt, budgetAmt, {
        tenantCode: tenant,
        fiscalYear: year,
        budgetKindCode: kind,
      }),
    };
  });
}

/** 단일 행 파생 재계산 */
export function withBaselineAmounts(row, dims, caches = null) {
  const filterDims = {
    acctClass: dims?.acctClass || '',
    budgetType: dims?.budgetType,
    inoutDiv: dims?.inoutDiv || '지출',
  };
  const prevMap =
    caches?.prevMap ||
    buildPrevYearAmtMap(dims?.tenantCode, dims?.fiscalYear, filterDims);
  const confMap =
    caches?.confMap ||
    buildConfirmedAmtMap(
      dims?.tenantCode,
      dims?.fiscalYear,
      dims?.budgetKindCode,
      filterDims
    );
  const key = buildBudgetDimKey(row, filterDims);
  const prevYearAmt = Number(prevMap.get(key)) || 0;
  const confirmedAmt = Number(confMap.get(key)) || 0;
  const budgetAmt = Number(row.budgetAmt) || 0;
  return {
    ...row,
    prevYearAmt,
    confirmedAmt,
    finalAmt: deriveFinalAmt(confirmedAmt, budgetAmt, {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      budgetKindCode: dims?.budgetKindCode,
    }),
  };
}

/** 저장용 — 파생 필드 제거 */
export function stripBaselineFields(row) {
  if (!row || typeof row !== 'object') return row;
  const next = { ...row };
  BASELINE_DERIVED_FIELDS.forEach((f) => {
    delete next[f];
  });
  return next;
}

export { FINAL_BUDGET_MODE };
